import Keycloak from 'keycloak-js';
import { config } from '../config.ts';

/**
 * Who is logged in, and a token to prove it with.
 *
 * An interface rather than the Keycloak object itself, because everything that
 * needs a token (the API client, the live stream in chapter 26) needs these
 * four things and nothing else, and a test can supply them without a Keycloak.
 */
export interface Session {
  /** A token valid for at least 30 more seconds, refreshed if necessary. */
  token(): Promise<string>;
  /** Refresh now, because the server just refused the current token. */
  refresh(): Promise<string>;
  /** For display. See `user` below for why that is all it is for. */
  readonly user: { name: string; username: string };
  logout(): Promise<void>;
}

export class LoginRequired extends Error {
  constructor() {
    super('login required');
    this.name = 'LoginRequired';
  }
}

/**
 * Log in with the Authorization Code flow and PKCE, or return to the login page.
 *
 * WHAT THE BROWSER HOLDS
 *
 * The realm's `stadtpuls-web` client is public: it has no secret, because a
 * secret shipped in a bundle is a published secret (config.ts). PKCE replaces
 * it. The browser makes up a random verifier for each login, sends only its
 * SHA-256 hash with the redirect, and must present the verifier itself to
 * exchange the code for tokens. An attacker who intercepts the code in the
 * redirect has the hash's input missing and cannot redeem it.
 *
 * Tokens stay in memory, which is keycloak-js's default and the one this code
 * relies on. Not localStorage: anything there survives the tab, is readable by
 * any script that ever runs on the origin, and turns one XSS into a stolen
 * session that outlives the page. The price is a redirect through Keycloak on
 * every reload, which is fast, because Keycloak's own session cookie answers it
 * without asking for a password.
 *
 * `checkLoginIframe: false`. The session-status iframe polls Keycloak from a
 * hidden frame, and browsers that block third-party cookies break it, which
 * makes a logged-in user look logged out at random. Token expiry does the same
 * job without the iframe: a revoked session fails its next refresh.
 */
export async function startSession(locale: string): Promise<Session> {
  const kc = new Keycloak({
    url: config.keycloak.url,
    realm: config.keycloak.realm,
    clientId: config.keycloak.clientId,
  });

  await kc.init({
    onLoad: 'login-required',
    pkceMethod: 'S256',
    checkLoginIframe: false,
    // The login page in the dashboard's language, via OIDC's ui_locales.
    locale,
  });

  const parsed = kc.tokenParsed as { name?: string; preferred_username?: string } | undefined;

  async function current(minValidity: number): Promise<string> {
    try {
      await kc.updateToken(minValidity);
    } catch {
      // The refresh token expired or the session was ended elsewhere. Nothing
      // on this page can fix that; the login page can.
      await kc.login();
      throw new LoginRequired();
    }
    if (!kc.token) throw new LoginRequired();
    return kc.token;
  }

  return {
    token: () => current(30),
    // -1 forces a refresh whatever the token's remaining lifetime.
    refresh: () => current(-1),
    /**
     * Read from the token WITHOUT verifying it, which is fine for a greeting and
     * worthless for a decision. Nothing in the dashboard branches on these
     * values: what the user may see is decided by the gateway, which verifies
     * the signature (chapter 15). A browser can be made to display anything.
     */
    user: {
      name: parsed?.name ?? parsed?.preferred_username ?? '',
      username: parsed?.preferred_username ?? '',
    },
    logout: () => kc.logout({ redirectUri: window.location.origin }),
  };
}
